import React, { useState, useEffect, useCallback, useRef } from 'react';
import { __ } from '@wordpress/i18n';
import { isEmpty, isNil, last, uniq, is, head, pathOr } from 'ramda';
import { Link, useNavigate } from 'react-router-dom';
import { klona } from 'klona';
import { wrap } from 'object-path-immutable';

// tools
import getBandoSeverity from '../../../../helpers/getBandoSeverity';
import getBandoLabel from '../../../../helpers/getBandoLabel';
import formatDateString from '../../../../helpers/formatDateString';

// api
import CompanyDocumentsService from '../../../../service/company-documents-service';
import CompanyService from '../../../../service/company-service';
import ApplicationService from '../../../../service/application-service';

// components
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Tag } from 'primereact/tag';
import ProperBandoLabel from '../../../../components/ProperBandoLabel';
import ChipWithIcon from '../../../../components/ChipWithIcon';
import { classNames } from 'primereact/utils';
import { FileUpload } from 'primereact/fileupload';
import { Dialog } from 'primereact/dialog';
import { ConfirmPopup, confirmPopup } from 'primereact/confirmpopup';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { Editor } from 'primereact/editor';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Checkbox } from 'primereact/checkbox';
import { Toast } from 'primereact/toast';


const DocumentsRequiredTable = ({ company, isocategories = [] }) => {
    const navigate = useNavigate();
    const chosenCompanyId = company.id;
    const [rawDocsData, setRawDocsData] = useState({});
    const [docs, setDocs] = useState([]);
    const [altroDocs, setAltroDocs] = useState([]);
    const [filters, setFilters] = useState(null);
    const [loading, setLoading] = useState(false);
    const [statuses, setStatuses] = useState([]);
    const [isVisibleAddNewDialog, setIsVisibleAddNewDialog] = useState(false);
    const [newFileData, setNewFileData] = useState({});
    const [fileAttached, setFileAttached] = useState([]);
    const [isVisibleReportDocDialog, setIsVisibleReportDocDialog] = useState(false);
    const [reportDocNote, setReportDocNote] = useState('');
    const [reportDocCategoryId, setReportDocCategoryId] = useState(null);
    const [isVisibleViewReportDialog, setIsVisibleViewReportDialog] = useState(false);
    const [viewReportNote, setViewReportNote] = useState('');
    const [viewReportDocId, setViewReportDocId] = useState(null);
    const toast = useRef(null);

    // ESG categories separated from main table
    const [esgCategories, setEsgCategories] = useState({ ESG1: [], ESG2: [] });

    // ISO categories repeater
    const [isoCategories, setIsoCategories] = useState([]);
    const [isoRows, setIsoRows] = useState([]); // [{ rowId, selectedCategoryId }]

    const [esgApplicationObj, setEsgApplicationObj] = useState(null);
    const [companyStatus, setCompanyStatus] = useState(company?.companyStatus ?? null);
    // Proposta di invio: quando il fascicolo e' completo si chiede al fornitore se vuole inviarlo.
    const [isVisibleInvioDialog, setIsVisibleInvioDialog] = useState(false);
    const invioPropostoRef = useRef(false);

    // ESG block visibility and user choices
    const [esgBlocks, setEsgBlocks] = useState({
        block1Visible: true,      // ESG1 block always visible initially
        block2Visible: false,     // Show when user selects "No" in Block 1
        block3Visible: false,     // Show when user selects "No" in Block 2
        block1NoSelected: false,  // Track "No" checkbox in Block 1
        block2NoSelected: false,  // Track "No" checkbox in Block 2
        block1HasUpload: false,   // Track if file uploaded in Block 1
        block2HasUpload: false    // Track if file uploaded in Block 2
    });

    const today = new Date();
    const tomorrow = new Date(today);

    useEffect(() => {
        if (!isEmpty(rawDocsData)) {
            const copyRawDocs = klona(rawDocsData);

            // Separate ESG and regular categories
            const esg1Categories = [];
            const esg2Categories = [];
            const regularCategories = rawDocsData.documentCategories;

            copyRawDocs?.esgCategories.forEach(cat => {
                if (cat.type === 'ESG1') {
                    esg1Categories.push(cat);
                } else if (cat.type === 'ESG2') {
                    esg2Categories.push(cat);
                }
            });

            // Format regular categories for table
            const formattedCategories = getFormattedData(
                regularCategories,
                [copyRawDocs.lookUpDataValue]
            );

            // Format ESG categories
            const formattedESG1 = getFormattedData(
                esg1Categories,
                [copyRawDocs.lookUpDataValue]
            );
            const formattedESG2 = getFormattedData(
                esg2Categories,
                [copyRawDocs.lookUpDataValue]
            );

            const sorted = [...formattedCategories]
                .filter(d => d.categoryName !== 'ALTRO')
                .sort((a, b) => 0);
            const altroData = formattedCategories.filter(d => d.categoryName === 'ALTRO');
            setDocs(sorted);
            setAltroDocs(altroData);
            setEsgCategories({ ESG1: formattedESG1, ESG2: formattedESG2 });
            setStatuses(uniq(formattedCategories.map(o => o.status)));
            initFilters();

            // Extract ISO categories from API response
            const isoFromApi = copyRawDocs.isocategories || [];
            setIsoCategories(isoFromApi);
            const preExisting = isoFromApi
                .filter(cat => cat.files && cat.files.length > 0)
                .map(cat => ({ rowId: `iso-${cat.id}`, selectedCategoryId: cat.id }));
            setIsoRows(preExisting);
        }
    }, [rawDocsData]);

    // Derive ESG block visibility from server data on load
    useEffect(() => {
        const esg1HasFiles = esgCategories.ESG1.length > 0 && !isEmpty(esgCategories.ESG1[0].files);
        const esg2HasFiles = esgCategories.ESG2.length > 0 && !isEmpty(esgCategories.ESG2[0].files);
        const hasApplication = esgApplicationObj !== null;

        if (esg1HasFiles) {
            setEsgBlocks({
                block1Visible: true,
                block2Visible: false,
                block3Visible: false,
                block1NoSelected: false,
                block2NoSelected: false,
                block1HasUpload: true,
                block2HasUpload: false
            });
        } else if (esg2HasFiles) {
            setEsgBlocks({
                block1Visible: true,
                block2Visible: true,
                block3Visible: false,
                block1NoSelected: true,
                block2NoSelected: false,
                block1HasUpload: false,
                block2HasUpload: true
            });
        } else if (hasApplication) {
            setEsgBlocks({
                block1Visible: true,
                block2Visible: true,
                block3Visible: true,
                block1NoSelected: true,
                block2NoSelected: true,
                block1HasUpload: false,
                block2HasUpload: false
            });
        } else {
            setEsgBlocks({
                block1Visible: true,
                block2Visible: false,
                block3Visible: false,
                block1NoSelected: false,
                block2NoSelected: false,
                block1HasUpload: false,
                block2HasUpload: false
            });
        }
    }, [esgCategories, esgApplicationObj]);

    useEffect(() => {
        if (isocategories && isocategories.length > 0) {
            setIsoCategories(isocategories);
            const preExisting = isocategories
                .filter(cat => cat.files && cat.files.length > 0)
                .map(cat => ({ rowId: `iso-${cat.id}`, selectedCategoryId: cat.id }));
            setIsoRows(preExisting);
        }
    }, [isocategories]);

    useEffect(() => {
        if (!loading && company) {
            getDocsRequest(chosenCompanyId);
            setEsgApplicationObj(null);
            ApplicationService.getApplications(getEsgApplCallback, errGetEsgApplCallback, [
                ['callId', 3],
                ['companyId', chosenCompanyId]
            ]);
        }
    }, [chosenCompanyId, company]);

    const getDocsRequest = (chosenCompanyId) => {
        setLoading(true);
        CompanyDocumentsService.getCompanyDocumentsByCategory(getCallback, errGetCallbacks, [
            ['companyId', chosenCompanyId]
        ]);
    }

    const getCallback = (resp) => {
        if (resp.status === 'SUCCESS' && resp.data) {
            setRawDocsData(resp.data);
        }
        setLoading(false);
    }

    const errGetCallbacks = () => {
        setLoading(false);
    }

    const getEsgApplCallback = (data) => {
        if (data.status === 'SUCCESS' && data.data.length) {
            const nonRejectedAppl = head(data.data.filter(o => !['REJECTED'].includes(o.status)));
            if (nonRejectedAppl) {
                setEsgApplicationObj(nonRejectedAppl);
            }
        }
    };

    const errGetEsgApplCallback = () => {};

    const handleCompileEsgModulo = () => {
        ApplicationService.createApplication(3, {}, createEsgApplCallback, errCreateEsgApplCallback, [
            ['companyId', chosenCompanyId]
        ]);
    };

    const createEsgApplCallback = (data) => {
        if (data.status === 'SUCCESS') {
            navigate(`/imieimoduli/${data.data.id}`);
        }
    };

    const errCreateEsgApplCallback = () => {};

    const getFormattedData = (data) => {
        return data.map((d, index) => {
            d.companyCategories = d.lookupData;
            d.uniqueRowId = `${d.id}-${index}`;
            if (d.categoryName !== 'ALTRO') {
                const lastFile = last(d.files);
                if (lastFile) {
                    d.fileId = lastFile.id;
                    d.json = lastFile.json;
                    d.statusDoc = lastFile.status;
                    // Il fornitore vede "Respinto" solo quando il rifiuto gli e' stato
                    // comunicato con una richiesta di integrazioni: prima l'istruttore
                    // sta ancora valutando e le motivazioni non sono partite.
                    d.statusEvaluation = lastFile.isValid
                        ? 'APPROVED'
                        : ((lastFile.validationNote && lastFile.rejectionNotifiedAt) ? 'REJECTED' : 'AWAITING');
                    d.createdDate = is(String, lastFile.createdDate) ? new Date(lastFile.createdDate) : (lastFile.createdDate ? lastFile.createdDate : '');
                    d.updatedDate = is(String, lastFile.updatedDate) ? new Date(lastFile.updatedDate) : (lastFile.updatedDate ? lastFile.updatedDate : '');
                    d.expirationDate = is(String, lastFile.expirationDate) ? new Date(lastFile.expirationDate) : (lastFile.expirationDate ? lastFile.expirationDate : '');
                } else {
                    d.statusDoc = 'NOT_AVAILABLE';
                    d.statusEvaluation = 'AWAITING';
                }
            }
            return d;
        });
    };

    const initFilters = () => {
        setFilters({
            categoryName: {
                operator: FilterOperator.AND,
                constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }]
            },
            createdDate: {
                operator: FilterOperator.AND,
                constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }]
            }
        });
    };

    const descriptionBodyTemplate = (rowData) => {
        return rowData.description;
    };

    const catBodyTemplate = (rowData) => {
        return rowData.companyCategories.map((v, index) => <ChipWithIcon key={`${rowData.uniqueRowId}-${v}-${index}`} value={v}/>);
    };

    const statusDocBodyTemplate = (rowData) => {
        return <ProperBandoLabel status={rowData.statusDoc}/>;
    };

    const statusEvaluationBodyTemplate = (rowData) => {
        return <ProperBandoLabel status={rowData.statusEvaluation}/>;
    };

    const createdDateBodyTemplate = (rowData) => {
        if (!rowData.createdDate || !(rowData.createdDate instanceof Date)) return null;
        return rowData.createdDate.toLocaleDateString('it-IT');
    };

    const statusFilterTemplate = (options) => {
        return <Dropdown value={options.value} options={statuses}
                         valueTemplate={getBandoLabel(options.value)}
                         onChange={(e) => options.filterCallback(e.value, options.index)}
                         itemTemplate={statusItemTemplate} placeholder={__('Scegli uno', 'htr')}
                         className="p-column-filter"/>;
    };

    const statusItemTemplate = (option) => {
        return <Tag value={getBandoLabel(option)} severity={getBandoSeverity(option)}/>;
    };

    const hideAddNewDialog = () => {
        setIsVisibleAddNewDialog(false);
        const newData = wrap({})
            .set(['documentType'], '')
            .set(['documentCategoryId'], 0)
            .set(['expirationDate'], '')
            .set(['name'], '')
            .value();
        setNewFileData(newData);
        setFileAttached([]);
    }

    const headerAddNewDialog = () => {
        return <span>{__('Aggiungi file', 'htr')}</span>
    }

    const headerReportDocDialog = () => {
        return <span>{__('Aggiungi nota sul documento', 'htr')}</span>
    }

    const hideReportDocDialog = () => {
        setIsVisibleReportDocDialog(false);
        setReportDocNote('');
    }

    const doReportDocumentNotAvailable = useCallback(() => {
        const formData = new FormData();
        formData.append(
            'companyDocumentRequest',
            new Blob([JSON.stringify({ motivation: reportDocNote })], { type: 'application/json' })
        );
        setLoading(true);
        CompanyDocumentsService.uploadCompanyDocumentNew(
            chosenCompanyId,
            formData,
            reportNotAvailableCallback,
            errReportNotAvailableCallback,
            [['documentCategoryId', reportDocCategoryId]]
        );
    }, [reportDocNote, reportDocCategoryId, chosenCompanyId]);

    const footerReportDocDialog = useCallback(() => {
        return <div>
            <Button type="button" label={__('Annulla', 'htr')} onClick={hideReportDocDialog} outlined/>
            <Button
                type="button"
                disabled={loading || isEmpty(reportDocNote) || isNil(reportDocNote)}
                label={__('Salva', 'htr')}
                onClick={doReportDocumentNotAvailable}/>
        </div>
    }, [reportDocNote, loading, doReportDocumentNotAvailable]);

    const renderReportDocEditorHeader = () => {
        return (
            <span className="ql-formats">
                <button className="ql-bold" aria-label="Bold"></button>
                <button className="ql-italic" aria-label="Italic"></button>
                <button className="ql-underline" aria-label="Underline"></button>
                <button className="ql-link" aria-label="Link"></button>
                <button className="ql-list" value="ordered"></button>
                <button className="ql-header" value="2"></button>
                <button className="ql-header" value="3"></button>
                <button className="ql-blockquote"></button>
                <button className="ql-list" value="bullet"></button>
                <button className="ql-indent" value="-1"></button>
                <button className="ql-indent" value="+1"></button>
            </span>
        );
    };

    const reportDocEditorHeader = renderReportDocEditorHeader();

    const footerAddNewDialog = () => {
        return <div>
            <Button type="button" label={__('Annulla', 'htr')} onClick={hideAddNewDialog} outlined/>
            <Button
                type="button"
                disabled={loading || !isValidForm()}
                label={__('Salva', 'htr')} onClick={doAddNew}/>
        </div>
    }

    const isInvalidField = (data, key) => isEmpty(data[key]) || isNil(data[key]);

    // DVR (document category 15): expiration date is optional
    const isDvrDocument = (data) => data?.name === 'DVR' || data?.documentCategoryId === 15;

    const onUpdateFieldValue = useCallback((value, name) => {
        const newData = wrap(newFileData).set([name], value).value();
        setNewFileData(newData);
    }, [newFileData]);

    const onFileSelect = (file) => {
        setFileAttached(file.files);
    }

    const isValidForm = useCallback(() => {
        const optionalKeys = isDvrDocument(newFileData) ? ['expirationDate'] : [];
        return !isEmpty(fileAttached) && !Object.keys(newFileData)
            .filter(k => !optionalKeys.includes(k))
            .filter(k => isInvalidField(newFileData, k) || newFileData[k] === 0).length
    }, [fileAttached, newFileData]);

    const doAddNew = useCallback(() => {
        const { documentCategoryId, ...rest } = newFileData;

        const hasExpiration = !isEmpty(newFileData.expirationDate) && !isNil(newFileData.expirationDate);
        const bodyData = {
            ...rest,
            expirationDate: hasExpiration ? formatDateString(newFileData.expirationDate) : null,
            rcExpirationDate: hasExpiration ? formatDateString(newFileData.expirationDate) : null
        };

        if (!isEmpty(fileAttached)) {
            const formData = new FormData();
            for (const file of fileAttached) {
                formData.append('files', file);
            }
            formData.append('companyDocumentRequest', new Blob([JSON.stringify(bodyData)], { type: 'application/json' }));
            setLoading(true);
            CompanyDocumentsService.uploadCompanyDocumentNew(
                chosenCompanyId,
                formData,
                uploadDoc,
                errUploadDoc,
                [['documentCategoryId', documentCategoryId]]
            );
        }
    }, [fileAttached, newFileData, chosenCompanyId]);

    const uploadDoc = (resp) => {
        if (resp.status === 'SUCCESS') {
            hideAddNewDialog();

            // Use resp.data to immediately update esgCategories if ESG document
            if (resp.data && resp.data.length > 0) {
                const uploadedFile = resp.data[0];
                const categoryType = uploadedFile?.category?.type;

                if (categoryType === 'ESG1' || categoryType === 'ESG2') {
                    const newRawDocs = klona(rawDocsData);
                    newRawDocs.esgCategories = newRawDocs.esgCategories.map(cat => {
                        if (cat.id === uploadedFile.category.id) {
                            cat.files = [...(cat.files || []), uploadedFile];
                        }
                        return cat;
                    });
                    setRawDocsData(newRawDocs);
                    updateEsgBlockVisibility(categoryType === 'ESG1' ? 1 : 2, 'uploaded');
                }

                if (categoryType && categoryType.startsWith('ISO')) {
                    setIsoCategories(prev => prev.map(cat => {
                        if (cat.id === uploadedFile.category.id) {
                            return { ...cat, files: [...(cat.files || []), uploadedFile] };
                        }
                        return cat;
                    }));
                }
            }

            CompanyService.getCompany(chosenCompanyId, getCpompanyCallback, () => {});
            getDocsRequest(chosenCompanyId);
        }
        setLoading(false);
    }

    const errUploadDoc = (resp) => {
        if (toast.current) {
            toast.current.show({
                severity: 'error',
                summary: '',
                detail: resp.message
            });
        }
        setLoading(false);
    }

    const reportNotAvailableCallback = (resp) => {
        setLoading(false);
        if (resp.status === 'SUCCESS') {
            hideReportDocDialog();
            getDocsRequest(chosenCompanyId);
        }
    };

    const errReportNotAvailableCallback = (resp) => {
        setLoading(false);
        if (toast.current) {
            toast.current.show({
                severity: 'error',
                summary: '',
                detail: resp.message
            });
        }
    };

    const getCpompanyCallback = (resp) => {
        if (resp.status === 'SUCCESS') {
            const { companyStatus } = resp.data;
            if ('APPROVED' === companyStatus) {
                navigate('/')
            }
        }
    }

    const onCreateNew = useCallback((categoryId, categoryName, categoryType) => {
        let newData = wrap({})
            .set(['documentType'], 'COMPANY_DOCUMENT')
            .set(['documentCategoryId'], categoryId)
            .set(['expirationDate'], '')
            .set(['name'], categoryName);

        // Add RC fields for insurance documents (all mandatory)
        if (categoryType === 'RC') {
            newData = newData
                .set(['insuranceCompany'], '')
                .set(['importoRCT'], null)
                .set(['importoRCO'], null);
        }

        setNewFileData(newData.value());
        setFileAttached([]);
        setIsVisibleAddNewDialog(true);
    }, []);

    const reportDocumentNotAvailable = (id) => {
        setReportDocCategoryId(id);
        setReportDocNote('');
        setIsVisibleReportDocDialog(true);
    }

    const openViewReportDialog = (rowData) => {
        const lastFile = last(rowData.files);
        setViewReportDocId(lastFile?.id || null);
        setViewReportNote(lastFile?.json?.motivation || '');
        setIsVisibleViewReportDialog(true);
    };

    const hideViewReportDialog = () => {
        setIsVisibleViewReportDialog(false);
        setViewReportNote('');
        setViewReportDocId(null);
    };

    const updateMotivationCallback = (resp) => {
        setLoading(false);
        if (resp.status === 'SUCCESS') {
            hideViewReportDialog();
            getDocsRequest(chosenCompanyId);
        }
    };

    const errUpdateMotivationCallback = (resp) => {
        setLoading(false);
        if (toast.current) {
            toast.current.show({ severity: 'error', summary: '', detail: resp.message });
        }
    };

    const doUpdateMotivation = useCallback(() => {
        setLoading(true);
        CompanyDocumentsService.updateCompanyDocument(
            viewReportDocId,
            { motivation: viewReportNote },
            updateMotivationCallback,
            errUpdateMotivationCallback
        );
    }, [viewReportDocId, viewReportNote]);

    const headerViewReportDialog = () => {
        return <span>{__('Motivazione documento non disponibile', 'htr')}</span>;
    };

    const footerViewReportDialog = () => {
        return <div>
            <Button type="button" label={__('Chiudi', 'htr')} onClick={hideViewReportDialog} outlined/>
            <Button type="button" label={__('Modifica', 'htr')} onClick={doUpdateMotivation} disabled={!viewReportDocId || loading}/>
        </div>;
    };

    const updateEsgBlockVisibility = useCallback((blockNum, action) => {
        setEsgBlocks(prev => {
            const updated = { ...prev };

            if (blockNum === 1) {
                if (action === 'uploaded') {
                    // Block 1 file uploaded: hide blocks 2 and 3 (done)
                    updated.block1HasUpload = true;
                    updated.block2Visible = false;
                    updated.block3Visible = false;
                    updated.block1NoSelected = false;
                } else if (action === 'file_deleted') {
                    // File was deleted, reset to initial state
                    updated.block1HasUpload = false;
                    // Don't automatically show block2 unless "No" was selected
                } else if (action === 'no_selected') {
                    // "No" selected in Block 1: show Block 2
                    updated.block1NoSelected = true;
                    updated.block2Visible = true;
                    updated.block1HasUpload = false;
                } else if (action === 'no_deselected') {
                    // "No" deselected in Block 1: hide Blocks 2 and 3
                    updated.block1NoSelected = false;
                    updated.block2Visible = false;
                    updated.block3Visible = false;
                }
            } else if (blockNum === 2) {
                if (action === 'uploaded') {
                    // Block 2 file uploaded: hide Block 3 (done)
                    updated.block2HasUpload = true;
                    updated.block3Visible = false;
                    updated.block2NoSelected = false;
                } else if (action === 'file_deleted') {
                    // File was deleted, reset to initial state
                    updated.block2HasUpload = false;
                } else if (action === 'no_selected') {
                    // "No" selected in Block 2: show Block 3
                    updated.block2NoSelected = true;
                    updated.block3Visible = true;
                    updated.block2HasUpload = false;
                } else if (action === 'no_deselected') {
                    // "No" deselected in Block 2: hide Block 3
                    updated.block2NoSelected = false;
                    updated.block3Visible = false;
                }
            }

            return updated;
        });
    }, []);

    const addIsoRow = useCallback(() => {
        setIsoRows(prev => [...prev, { rowId: `iso-row-${Date.now()}`, selectedCategoryId: null }]);
    }, []);

    const removeIsoRow = useCallback((rowId) => {
        setIsoRows(prev => prev.filter(r => r.rowId !== rowId));
    }, []);

    const setIsoRowCategory = useCallback((rowId, categoryId) => {
        setIsoRows(prev => prev.map(r => r.rowId === rowId ? { ...r, selectedCategoryId: categoryId } : r));
    }, []);

    // Il documento e' scaduto o in scadenza: lo dice lo "Stato Scadenza" (status del file,
    // VALID / DUE / EXPIRED); se manca, si calcola dalla data di scadenza con la stessa
    // soglia del backend (15 giorni).
    const isExpiredOrDue = (row) => {
        if (['EXPIRED', 'DUE'].includes(row.statusDoc)) return true;
        if (['VALID', 'NOT_AVAILABLE'].includes(row.statusDoc)) return false;
        const exp = is(String, row.expirationDate) ? new Date(row.expirationDate) : row.expirationDate;
        if (!(exp instanceof Date) || isNaN(exp.getTime())) return false;
        const limit = new Date();
        limit.setDate(limit.getDate() + 15);
        return exp <= limit;
    };

    // "Aggiorna" e' spento solo se il documento e' approvato e non e' scaduto ne' in scadenza.
    const isUpdateDisabled = (row) => row.statusEvaluation === 'APPROVED' && !isExpiredOrDue(row);

    const actionsBodyTemplate = (rowData) => {
        const file = head(rowData.files);
        const motivation = pathOr(null, ['json', 'motivation'], rowData);
        return <div className="appPageSection__iconActions">
            {rowData.statusDoc === 'NOT_AVAILABLE'
                ? <Button icon="pi pi-upload" rounded
                          onClick={() => onCreateNew(rowData.id, rowData.description, rowData.type)}
                          severity="info"
                          aria-label={__('Carica', 'htr')}/> : null}
            {rowData.statusDoc !== 'NOT_AVAILABLE'
                ? <Button icon="pi pi-sync" rounded
                          disabled={isUpdateDisabled(rowData)}
                          onClick={() => onCreateNew(rowData.id, rowData.description, rowData.type)}
                          outlined severity="info"
                          aria-label={__('Aggiorna', 'htr')}/> : null}
            {rowData.statusDoc === 'NOT_AVAILABLE'
                ? <Button
                        icon="pi pi-file-edit" rounded
                        onClick={() => reportDocumentNotAvailable(rowData.id)}
                        severity="warning"
                        tooltip="Aggiungi/Modifica nota"
                        tooltipOptions={{ position: 'top' }}
                        aria-label={__('Aggiungi/Modifica nota', 'htr')}/> : null}
            {rowData.statusDoc !== 'NOT_AVAILABLE' && !motivation
                ? <Button icon="pi pi-eye" rounded
                    disabled={rowData.statusDoc === 'NOT_AVAILABLE' || isNil(file.filePath)}
                    onClick={() => {
                        if (file) {
                            window.open(file.filePath, '_blank').focus();
                        }
                    }}
                    outlined severity="info"
                    aria-label={__('Mostra', 'htr')}/> : null}
            {motivation
                ? <Button icon="pi pi-pencil" rounded
                    onClick={() => openViewReportDialog(rowData)}
                    outlined severity="info"
                    aria-label={__('Visualizza motivazione', 'htr')}/> : null}
            <ConfirmPopup/>
            {'RECEIVED' === company.companyStatus
                ? <Button icon="pi pi-trash" rounded
                    disabled={rowData.statusEvaluation === 'APPROVED' || rowData.statusDoc === 'NOT_AVAILABLE'}
                    onClick={(event) => confirmDelete(event, rowData.fileId)}
                    outlined severity="danger"
                    aria-label={__('Cancella', 'htr')}/> : null}
        </div>
    }

    const esgActionsTemplate = (category, esgType) => {
        const file = head(category.files);
        return (
            <div className="appPageSection__iconActions">
                {category.statusDoc === 'NOT_AVAILABLE'
                    ? <Button icon="pi pi-upload" rounded
                              onClick={() => onCreateNew(category.id, category.description, category.type)}
                              severity="info"
                              aria-label={__('Carica', 'htr')}/> : null}
                {category.statusDoc !== 'NOT_AVAILABLE'
                    ? <Button icon="pi pi-sync" rounded
                              disabled={isUpdateDisabled(category)}
                              onClick={() => onCreateNew(category.id, category.description, category.type)}
                              outlined severity="info"
                              aria-label={__('Aggiorna', 'htr')}/> : null}
                {category.statusDoc !== 'NOT_AVAILABLE'
                    ? <Button icon="pi pi-eye" rounded
                        onClick={() => {
                            if (file) {
                                window.open(file.filePath, '_blank').focus();
                            }
                        }}
                        outlined severity="info"
                        aria-label={__('Mostra', 'htr')}/> : null}
                {'RECEIVED' === company.companyStatus
                    ? <Button icon="pi pi-trash" rounded
                        disabled={category.statusEvaluation === 'APPROVED' || category.statusDoc === 'NOT_AVAILABLE'}
                        onClick={(event) => confirmDelete(event, category.fileId)}
                        outlined severity="danger"
                        aria-label={__('Cancella', 'htr')}/> : null}
                <ConfirmPopup/>
            </div>
        );
    };

    const confirmDelete = (event, id) => {
        confirmPopup({
            target: event.currentTarget,
            message: __('Sei sicuro di voler rimuovere il file?', 'htr'),
            acceptLabel: __('Si', 'htr'),
            icon: 'pi pi-info-circle',
            defaultFocus: 'reject',
            acceptClassName: 'p-button-danger',
            accept: () => {
                handleDeleteFile(id);
            },
            reject: () => {
            }
        });
    };

    const handleDeleteFile = (id) => {
        setLoading(true);
        CompanyDocumentsService.deleteCompanyDocument(id,(resp) => deleteCallback(resp, id), errDeleteCallback)
    }

    const deleteCallback = useCallback((resp, deletedId) => {
        if (resp.status === 'SUCCESS') {
            let newRawDocs = klona(rawDocsData);
            newRawDocs.documentCategories = newRawDocs.documentCategories.map(cat => {
                cat.files = cat.files.filter(f => f.id !== deletedId);
                return cat;
            });
            // Also remove from ESG categories
            newRawDocs.esgCategories = newRawDocs.esgCategories.map(cat => {
                cat.files = cat.files.filter(f => f.id !== deletedId);
                return cat;
            });
            setRawDocsData(newRawDocs);

            // Remove from ISO categories (local state, not rawDocsData)
            setIsoCategories(prev => prev.map(cat => ({
                ...cat,
                files: (cat.files || []).filter(f => f.id !== deletedId)
            })));

            // Update ESG block visibility — search esgCategories (was incorrectly documentCategories)
            const esg1Cat = newRawDocs.esgCategories.find(cat => cat.type === 'ESG1');
            const esg2Cat = newRawDocs.esgCategories.find(cat => cat.type === 'ESG2');

            if (esg1Cat && isEmpty(esg1Cat.files)) {
                updateEsgBlockVisibility(1, 'file_deleted');
            }
            if (esg2Cat && isEmpty(esg2Cat.files)) {
                updateEsgBlockVisibility(2, 'file_deleted');
            }
        }
        setLoading(false);
    }, [rawDocsData, updateEsgBlockVisibility]);

    const errDeleteCallback = () => {
        setLoading(false);
    }

    const allRegularCategoriesHaveFiles = useCallback(() => {
        return docs.every(doc => doc.statusDoc !== 'NOT_AVAILABLE');
    }, [docs]);

    const hasEsgFiles = useCallback(() => {
        const esg1HasFiles = esgCategories.ESG1.length > 0 &&
                            !isEmpty(esgCategories.ESG1[0].files);
        const esg2HasFiles = esgCategories.ESG2.length > 0 &&
                            !isEmpty(esgCategories.ESG2[0].files);
        return esg1HasFiles || esg2HasFiles;
    }, [esgCategories]);

    const shouldShowSubmitButton = useCallback(() => {
        const allRegularDone = allRegularCategoriesHaveFiles();
        if (!allRegularDone) return false;
        if (hasEsgFiles()) return true;
        return esgApplicationObj?.status === 'SUBMIT';
    }, [allRegularCategoriesHaveFiles, hasEsgFiles, esgApplicationObj]);

    const getSubmitDisabledReason = useCallback(() => {
        if (companyStatus !== 'RECEIVED') {
            return __('Invio non disponibile: lo stato dell\'azienda non consente l\'invio dei documenti.', 'htr');
        }
        if (!allRegularCategoriesHaveFiles()) {
            return __('Carica tutti i documenti per poter procedere all\'invio.', 'htr');
        }
        if (!hasEsgFiles() && esgApplicationObj?.status !== 'SUBMIT') {
            return __('I documenti ESG non sono completi: carica i file ESG o completa l\'applicazione ESG prima di inviare.', 'htr');
        }
        return null;
    }, [companyStatus, allRegularCategoriesHaveFiles, hasEsgFiles, esgApplicationObj]);

    // Fascicolo completo e azienda ancora in RECEIVED: si propone l'invio una volta sola
    // (si ripropone se il fornitore rimuove un documento e lo ricarica).
    useEffect(() => {
        if (loading || companyStatus !== 'RECEIVED' || isEmpty(docs)) return;
        if (!shouldShowSubmitButton()) {
            invioPropostoRef.current = false;
            return;
        }
        if (invioPropostoRef.current) return;
        invioPropostoRef.current = true;
        setIsVisibleInvioDialog(true);
    }, [loading, companyStatus, docs, esgCategories, esgApplicationObj, shouldShowSubmitButton]);

    const submitDocuments = () => {
        setLoading(true);
        CompanyDocumentsService.submitCompanyDocument(
            chosenCompanyId,
            submitDocumentsCallback,
            errSubmitDocumentsCallback
        );
    };

    const submitDocumentsCallback = (resp) => {
        setLoading(false);
        if (toast.current) {
            toast.current.show({
                severity: resp.status === 'SUCCESS' ? 'success' : 'error',
                summary: '',
                detail: resp.message
            });
        }
        if (resp.status === 'SUCCESS') {
            if (resp.data?.companyStatus) {
                setCompanyStatus(resp.data.companyStatus);
            }
            getDocsRequest(chosenCompanyId);
        }
    };

    const errSubmitDocumentsCallback = (resp) => {
        setLoading(false);
        if (toast.current) {
            toast.current.show({
                severity: 'error',
                summary: '',
                detail: resp.message
            });
        }
    };

    return (
        <div className="appPageSection__table">
            <DataTable value={docs} showGridlines stripedRows rows={5} loading={loading} dataKey="uniqueRowId"
                       filters={filters} removableSort
                       onFilter={(e) => setFilters(e.filters)}>
                <Column field="description"
                        header={__('Documento', 'htr')}
                        body={descriptionBodyTemplate}
                        filter filterField="name"
                        filterPlaceholder={__('Cerca per nome', 'htr')}
                        style={{ minWidth: '12rem' }}/>
                <Column body={catBodyTemplate} header={__('Categoria', 'htr')}
                        style={{ minWidth: '8rem' }}/>
                <Column field="statusDoc" header={__('Stato Scadenza', 'htr')}
                        filterMenuStyle={{ width: '14rem' }}
                        style={{ width: '120px' }} body={statusDocBodyTemplate}
                        filterElement={statusFilterTemplate}/>
                <Column field="statusEvaluation" header={__('Stato Verifica', 'htr')}
                        filterMenuStyle={{ width: '14rem' }}
                        style={{ width: '120px' }} body={statusEvaluationBodyTemplate}
                        filterElement={statusFilterTemplate}/>
                <Column field="createdDate"
                        header={__('Data Creazione', 'htr')}
                        style={{ width: '140px' }}
                        body={createdDateBodyTemplate}/>
                <Column header={__('Azioni', 'htr')}
                        body={actionsBodyTemplate}/>
            </DataTable>
            <Toast ref={toast}/>
            <ConfirmDialog
                group="declarative"
                visible={isVisibleInvioDialog}
                onHide={() => setIsVisibleInvioDialog(false)}
                header={__('Documenti completi', 'htr')}
                message={__('Hai caricato tutti i documenti richiesti. Vuoi inviarli adesso?', 'htr')}
                icon="pi pi-send"
                accept={submitDocuments}
                acceptLabel={__('Invia', 'htr')}
                reject={() => setIsVisibleInvioDialog(false)}
                rejectLabel={__('Più tardi', 'htr')}
                style={{ maxWidth: '500px' }}/>

            {altroDocs.length > 0 && (
                <div style={{ marginTop: '2rem' }}>
                    <div className="appPageSection__hr">
                        <span>{__('Documenti Aggiuntivi', 'htr')}</span>
                    </div>
                    {altroDocs.map(altroDoc => (
                        <div key={altroDoc.id} style={{ marginTop: '1rem', padding: '1.5rem', border: '1px solid #ddd', borderRadius: '4px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                <h3 style={{ margin: 0 }}>{altroDoc.description}</h3>
                                <div className="appPageSection__iconActions">
                                    <Button icon="pi pi-upload" rounded severity="info"
                                        onClick={() => onCreateNew(altroDoc.id, altroDoc.description, altroDoc.type)}
                                        aria-label={__('Carica', 'htr')}/>
                                    <Button icon="pi pi-file-edit" rounded severity="warning"
                                        onClick={() => reportDocumentNotAvailable(altroDoc.id)}
                                            tooltip="Aggiungi/Modifica nota"
                                            tooltipOptions={{ position: 'top' }}
                                        aria-label={__('Aggiungi/Modifica nota', 'htr')}/>
                                    <ConfirmPopup/>
                                </div>
                            </div>
                            {altroDoc.files?.length > 0 ? (
                                <DataTable value={altroDoc.files} showGridlines dataKey="id">
                                    <Column header={__('Nome', 'htr')} style={{ width: '45%' }}
                                            body={(f) => f.fileName}/>
                                    <Column header={__('Stato Scadenza', 'htr')} style={{ width: '130px' }}
                                        body={(f) => <ProperBandoLabel status={f.status}/>}/>
                                    <Column header={__('Stato Verifica', 'htr')} style={{ width: '130px' }}
                                        body={(f) => <ProperBandoLabel status={f.isValid ? 'APPROVED' : 'AWAITING'}/>}/>
                                    <Column header={__('Data Scadenza', 'htr')} style={{ width: '140px' }}
                                        body={(f) => {
                                            const d = is(String, f.expirationDate) ? new Date(f.expirationDate) : f.expirationDate;
                                            return d instanceof Date ? d.toLocaleDateString('it-IT') : '-';
                                        }}/>
                                    <Column header={__('Azioni', 'htr')}
                                        body={(f) => (
                                            <div className="appPageSection__iconActions">
                                                <Button icon="pi pi-eye" rounded outlined severity="info"
                                                    disabled={!f.filePath}
                                                    onClick={() => f.filePath && window.open(f.filePath, '_blank').focus()}
                                                    aria-label={__('Mostra', 'htr')}/>
                                                {'RECEIVED' === company.companyStatus &&
                                                    <Button icon="pi pi-trash" rounded outlined severity="danger"
                                                        disabled={f.isValid}
                                                        onClick={(e) => confirmDelete(e, f.id)}
                                                        aria-label={__('Cancella', 'htr')}/>}
                                            </div>
                                        )}/>
                                </DataTable>
                            ) : (
                                <p style={{ color: 'var(--text-color-secondary)', margin: 0 }}>
                                    {__('Nessun file caricato', 'htr')}
                                </p>
                            )}
                            <div className="appPageSection__iconActions" style={{justifyContent: 'right', marginTop: '10px'}}>
                                <Button severity="info"
                                        onClick={() => onCreateNew(altroDoc.id, altroDoc.description, altroDoc.type)}>
                                    {__('Aggiungi Altro Documento', 'htr')}</Button>
                                <ConfirmPopup/>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* ISO Categories Repeater */}
            {(() => {
                const usedIsoCategoryIds = isoRows
                    .filter(r => r.selectedCategoryId !== null)
                    .map(r => r.selectedCategoryId);
                const allIsoTypesUsed = usedIsoCategoryIds.length >= isoCategories.length;

                return isoCategories.length > 0 && (
                    <div style={{ marginTop: '2rem' }}>
                        <div className="appPageSection__hr">
                            <span>{__('Documenti ISO', 'htr')}</span>
                        </div>

                        {isoRows.map(row => {
                            const selectedCat = isoCategories.find(c => c.id === row.selectedCategoryId);
                            const availableForRow = isoCategories.filter(
                                cat => cat.id === row.selectedCategoryId ||
                                       !usedIsoCategoryIds.includes(cat.id)
                            );

                            return (
                                <div key={row.rowId} style={{ marginTop: '1rem', padding: '1.5rem', border: '1px solid #ddd', borderRadius: '4px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                        <div style={{ flex: 1, marginRight: '1rem' }}>
                                            <Dropdown
                                                value={row.selectedCategoryId}
                                                options={availableForRow}
                                                optionLabel="description"
                                                optionValue="id"
                                                placeholder={__('Seleziona tipo documento ISO', 'htr')}
                                                disabled={row.selectedCategoryId !== null}
                                                onChange={(e) => setIsoRowCategory(row.rowId, e.value)}
                                                style={{ width: '100%' }}
                                            />
                                        </div>
                                        <div className="appPageSection__iconActions">
                                            {selectedCat && (
                                                <Button icon="pi pi-upload" rounded severity="info"
                                                    onClick={() => onCreateNew(selectedCat.id, selectedCat.description, selectedCat.type)}
                                                    aria-label={__('Carica', 'htr')}/>
                                            )}
                                            <Button icon="pi pi-trash" rounded severity="danger" outlined
                                                onClick={() => removeIsoRow(row.rowId)}
                                                aria-label={__('Rimuovi riga', 'htr')}/>
                                            <ConfirmPopup/>
                                        </div>
                                    </div>

                                    {selectedCat && selectedCat.files && selectedCat.files.length > 0 ? (
                                        <DataTable value={selectedCat.files} showGridlines dataKey="id">
                                            <Column header={__('Nome', 'htr')} style={{ width: '45%' }}
                                                    body={(f) => f.fileName}/>
                                            <Column header={__('Stato Scadenza', 'htr')} style={{ width: '130px' }}
                                                body={(f) => <ProperBandoLabel status={f.status}/>}/>
                                            <Column header={__('Stato Verifica', 'htr')} style={{ width: '130px' }}
                                                body={(f) => <ProperBandoLabel status={f.isValid ? 'APPROVED' : 'AWAITING'}/>}/>
                                            <Column header={__('Data Scadenza', 'htr')} style={{ width: '140px' }}
                                                body={(f) => {
                                                    const d = is(String, f.expirationDate) ? new Date(f.expirationDate) : f.expirationDate;
                                                    return d instanceof Date ? d.toLocaleDateString('it-IT') : '-';
                                                }}/>
                                            <Column header={__('Azioni', 'htr')}
                                                body={(f) => (
                                                    <div className="appPageSection__iconActions">
                                                        <Button icon="pi pi-eye" rounded outlined severity="info"
                                                            disabled={!f.filePath}
                                                            onClick={() => f.filePath && window.open(f.filePath, '_blank').focus()}
                                                            aria-label={__('Mostra', 'htr')}/>
                                                        {'RECEIVED' === company.companyStatus &&
                                                            <Button icon="pi pi-trash" rounded outlined severity="danger"
                                                                disabled={f.isValid}
                                                                onClick={(e) => confirmDelete(e, f.id)}
                                                                aria-label={__('Cancella', 'htr')}/>}
                                                    </div>
                                                )}/>
                                        </DataTable>
                                    ) : selectedCat ? (
                                        <p style={{ color: 'var(--text-color-secondary)', margin: 0 }}>
                                            {__('Nessun file caricato', 'htr')}
                                        </p>
                                    ) : null}
                                </div>
                            );
                        })}

                        <div style={{ marginTop: '1rem' }}>
                            <Button severity="info"
                                disabled={allIsoTypesUsed}
                                onClick={addIsoRow}>
                                {__('Aggiungi Documento ISO', 'htr')}
                            </Button>
                        </div>
                    </div>
                );
            })()}

            {/* ESG Block 1: Financial Statement Available (ESG1) */}
            {esgBlocks.block1Visible && esgCategories.ESG1.length > 0 && (
                <div className="appPageSection__esgBlock" style={{ marginTop: '2rem', padding: '1.5rem', border: '1px solid #ddd', borderRadius: '4px' }}>
                    <h3>{esgCategories.ESG1[0].description}</h3>

                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem' }}>
                        <div>
                            <strong>{__('Stato Scadenza:', 'htr')}</strong>{' '}
                            <ProperBandoLabel status={esgCategories.ESG1[0].statusDoc}/>
                        </div>
                        <div>
                            <strong>{__('Stato Verifica:', 'htr')}</strong>{' '}
                            <ProperBandoLabel status={esgCategories.ESG1[0].statusEvaluation}/>
                        </div>
                    </div>

                    {!esgBlocks.block1NoSelected && esgActionsTemplate(esgCategories.ESG1[0], 'ESG1')}

                    <div className="appForm__cols" style={{ marginTop: '1rem' }}>
                        <div className="appForm__field">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <Checkbox
                                    inputId="esg1_no"
                                    checked={esgBlocks.block1NoSelected}
                                    disabled={esgBlocks.block1HasUpload || esgBlocks.block2HasUpload || esgApplicationObj !== null}
                                    onChange={(e) => {
                                        if (e.checked) {
                                            updateEsgBlockVisibility(1, 'no_selected');
                                        } else {
                                            updateEsgBlockVisibility(1, 'no_deselected');
                                        }
                                    }}
                                />
                                <label htmlFor="esg1_no">
                                    {__('No, non ho questo documento', 'htr')}
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ESG Block 2: Indices/Certifications Available (ESG2) */}
            {esgBlocks.block2Visible && esgCategories.ESG2.length > 0 && (
                <div className="appPageSection__esgBlock" style={{ marginTop: '2rem', padding: '1.5rem', border: '1px solid #ddd', borderRadius: '4px' }}>
                    <h3>{esgCategories.ESG2[0].description}</h3>

                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem' }}>
                        <div>
                            <strong>{__('Stato Scadenza:', 'htr')}</strong>{' '}
                            <ProperBandoLabel status={esgCategories.ESG2[0].statusDoc}/>
                        </div>
                        <div>
                            <strong>{__('Stato Verifica:', 'htr')}</strong>{' '}
                            <ProperBandoLabel status={esgCategories.ESG2[0].statusEvaluation}/>
                        </div>
                    </div>

                    {!esgBlocks.block2NoSelected && esgActionsTemplate(esgCategories.ESG2[0], 'ESG2')}

                    <div className="appForm__cols" style={{ marginTop: '1rem' }}>
                        <div className="appForm__field">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <Checkbox
                                    inputId="esg2_no"
                                    checked={esgBlocks.block2NoSelected}
                                    disabled={esgBlocks.block2HasUpload || esgApplicationObj !== null}
                                    onChange={(e) => {
                                        if (e.checked) {
                                            updateEsgBlockVisibility(2, 'no_selected');
                                        } else {
                                            updateEsgBlockVisibility(2, 'no_deselected');
                                        }
                                    }}
                                />
                                <label htmlFor="esg2_no">
                                    {__('No, non ho questo documento', 'htr')}
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ESG Block 3: No Existing ESG Documentation */}
            {esgBlocks.block3Visible && (
                <div className="appPageSection__esgBlock" style={{ marginTop: '2rem', padding: '1.5rem', border: '1px solid #ddd', borderRadius: '4px' }}>
                    <h3>{esgApplicationObj && esgApplicationObj.status === 'EVALUATION' ? __('Documentazione ESG', 'htr') : __('Nessuna Documentazione ESG Esistente', 'htr')}</h3>
                    <p>{esgApplicationObj && esgApplicationObj.status === 'EVALUATION' ? __('Documentazione ESG caricata con successo.', 'htr') : __('Non disponi di documentazione ESG. Puoi richiedere un modulo di valutazione ESG.', 'htr')}</p>
                    {!(esgApplicationObj && esgApplicationObj.status === 'EVALUATION') && (
                        esgApplicationObj && esgApplicationObj.id ? (
                            <Button
                                label={__('Richiedi Modulo di Valutazione ESG', 'htr')}
                                icon="pi pi-arrow-right"
                                iconPos="right"
                                disabled={companyStatus !== 'RECEIVED' && esgApplicationObj.status !== 'DRAFT'}
                                onClick={() => navigate(`/imieimoduli/${esgApplicationObj.id}`)}
                            />
                        ) : (
                            <Button
                                label={__('Compila', 'htr')}
                                icon="pi pi-save"
                                iconPos="right"
                                disabled={companyStatus !== 'RECEIVED'}
                                onClick={handleCompileEsgModulo}
                            />
                        )
                    )}
                </div>
            )}

            <div className="appPage__spacer"></div>

            <div className="appPageSection__hr">
                <span>{__('Azioni', 'htr')}</span>
            </div>

            <div className="appPage__spacer"></div>

            <div className="appPageSection">
                <div className="appPageSection__actions">
                    <Button
                            type="button"
                            disabled={!shouldShowSubmitButton() || companyStatus !== 'RECEIVED'}
                            onClick={submitDocuments}
                            label={__('Invia', 'htr')}
                            icon="pi pi-check"
                            iconPos="right"
                            tooltip={getSubmitDisabledReason()}
                            tooltipOptions={{ position: 'top', showOnDisabled: true }}
                        />
                    {esgApplicationObj && esgApplicationObj.id && esgApplicationObj.status !== 'DRAFT' ?
                        <Link to={`/domande/${esgApplicationObj.id}/preview`}>
                            <Button
                                severity="info"
                                outlined
                                label={__('Anteprima Modulo di Valutazione ESG', 'htr')}
                                icon="pi pi-eye"
                                iconPos="right"/>
                        </Link>
                    : null}
                </div>
            </div>

            <div className="appPage__spacer"></div>

            {companyStatus && companyStatus !== 'RECEIVED' && (
                <div className="appPageSection__message info">
                    <i className="pi pi-info-circle"></i>
                    <span>
                        {__('Stato pratica:', 'htr')}{' '}
                        <strong><ProperBandoLabel status={companyStatus} /></strong>
                        {' — '}{__('Non è possibile inviare documenti o aprire una nuova richiesta ESG in questo stato.', 'htr')}
                    </span>
                </div>
            )}

            <Dialog
                visible={isVisibleAddNewDialog}
                modal
                header={headerAddNewDialog}
                footer={footerAddNewDialog}
                style={{ maxWidth: '600px', width: '100%' }}
                onHide={hideAddNewDialog}>
                <div className="appForm__cols">
                    <div className="appForm__field">
                        <label
                            className={classNames({ 'p-error': !isDvrDocument(newFileData) && (isEmpty(newFileData.expirationDate) || isNil(newFileData.expirationDate)) })}>
                            {__('Scadenza', 'htr')}{isDvrDocument(newFileData) ? ` (${__('opzionale', 'htr')})` : '*'}
                        </label>
                        <Calendar
                            value={newFileData.expirationDate}
                            minDate={tomorrow}
                            invalid={!isDvrDocument(newFileData) && (isEmpty(newFileData.expirationDate) || isNil(newFileData.expirationDate) || newFileData.expirationDate === 0)}
                            onChange={(e) => onUpdateFieldValue(e.value, 'expirationDate')}/>
                    </div>
                </div>

                {/* RC-specific fields - only shown when type is RC */}
                {newFileData.insuranceCompany !== undefined && (
                    <>
                        <div className="appForm__cols">
                            <div className="appForm__field">
                                <label className={classNames({
                                    'p-error': isInvalidField(newFileData, 'insuranceCompany')
                                })}>
                                    {__('Compagnia Assicurativa', 'htr')}*
                                </label>
                                <InputText
                                    value={newFileData.insuranceCompany}
                                    invalid={isInvalidField(newFileData, 'insuranceCompany')}
                                    onChange={(e) => onUpdateFieldValue(e.target.value, 'insuranceCompany')}
                                />
                            </div>
                        </div>
                        <div className="appForm__cols">
                            <div className="appForm__field">
                                <label className={classNames({
                                    'p-error': isInvalidField(newFileData, 'importoRCT')
                                })}>
                                    {__('Importo RCT', 'htr')}*
                                </label>
                                <div className="p-inputgroup">
                                    <span className="p-inputgroup-addon">€</span>
                                    <InputNumber
                                        value={newFileData.importoRCT}
                                        onValueChange={(e) => onUpdateFieldValue(e.value, 'importoRCT')}
                                        mode="decimal"
                                        locale="it-IT"
                                        minFractionDigits={2}
                                        maxFractionDigits={2}
                                        invalid={isInvalidField(newFileData, 'importoRCT')}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="appForm__cols">
                            <div className="appForm__field">
                                <label className={classNames({
                                    'p-error': isInvalidField(newFileData, 'importoRCO')
                                })}>
                                    {__('Importo RCO', 'htr')}*
                                </label>
                                <div className="p-inputgroup">
                                    <span className="p-inputgroup-addon">€</span>
                                    <InputNumber
                                        value={newFileData.importoRCO}
                                        onValueChange={(e) => onUpdateFieldValue(e.value, 'importoRCO')}
                                        mode="decimal"
                                        locale="it-IT"
                                        minFractionDigits={2}
                                        maxFractionDigits={2}
                                        invalid={isInvalidField(newFileData, 'importoRCO')}
                                    />
                                </div>
                            </div>
                        </div>
                    </>
                )}

                <div className="appForm__cols">
                    <div className="appForm__field">
                        <label
                            className={classNames({ 'p-error': isEmpty(newFileData.file) || isNil(newFileData.expirationDate) })}>
                            {__('File', 'htr')}*
                        </label>
                        <FileUpload
                            mode="basic"
                            name="file"
                            onSelect={onFileSelect}/>
                    </div>
                </div>
            </Dialog>

            <Dialog
                visible={isVisibleReportDocDialog}
                modal
                header={headerReportDocDialog}
                footer={footerReportDocDialog}
                style={{ maxWidth: '600px', width: '100%' }}
                onHide={hideReportDocDialog}>
                <div className="appForm__field">
                    <label
                        className={classNames({ 'p-error': isEmpty(reportDocNote) || isNil(reportDocNote) })}>
                        {__('Nota', 'htr')}*
                    </label>
                    <div translate="no">
                        <Editor
                            value={reportDocNote}
                            readOnly={false}
                            placeholder={__('Inserisci qui la nota', 'htr')}
                            headerTemplate={reportDocEditorHeader}
                            onTextChange={(e) => setReportDocNote(e.htmlValue)}
                            style={{ height: 240, width: '100%' }}
                        />
                    </div>
                </div>
            </Dialog>

            <Dialog
                visible={isVisibleViewReportDialog}
                modal
                header={headerViewReportDialog}
                footer={footerViewReportDialog}
                style={{ maxWidth: '600px', width: '100%' }}
                onHide={hideViewReportDialog}>
                <div className="appForm__field">
                    <label>{__('Nota', 'htr')}</label>
                    <div translate="no">
                        <Editor
                            value={viewReportNote}
                            onTextChange={(e) => setViewReportNote(e.htmlValue)}
                            headerTemplate={reportDocEditorHeader}
                            style={{ height: 240, width: '100%' }}
                        />
                    </div>
                </div>
            </Dialog>
        </div>
    )
}

export default DocumentsRequiredTable;
